import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LOCALES, pickBilingual, type Locale } from '@lims/shared-types';
import { authApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ErrorState,
  Form,
  FormSection,
  hasNewPasswordProblem,
  NewPasswordFields,
  PageHeader,
  Panel,
  PasswordField,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { changeLanguage, currentLocale } from '../../i18n/index.js';
import { PermissionSummary } from '../users/PermissionSummary.js';

/**
 * The caller's own profile, in four parts:
 *
 *   1. who they are — name, address, role and laboratory at a glance;
 *   2. what they may change — display name and interface language;
 *   3. their password — changed with the CURRENT password (D181), which ends
 *      every other session and keeps this one;
 *   4. what they may do — their effective permissions, grouped by module.
 *
 * Role, activation and permissions are deliberately read-only here — they are
 * granted by a manager, and the server rejects a self-service attempt to
 * change them regardless of what this page renders.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const locale = currentLocale();

  if (!user) return null;

  const laboratoryName = user.tenant
    ? pickBilingual({ ar: user.tenant.nameAr, en: user.tenant.nameEn }, locale)
    : null;

  return (
    <>
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />

      <div className="flex min-w-0 flex-col gap-gutter">
        {/* 1. Summary */}
        <section
          aria-label={t('profile.sectionLaboratory')}
          className="flex min-w-0 flex-wrap items-center gap-stack rounded-large border border-default-200 bg-white p-4 sm:p-6"
        >
          <span
            aria-hidden="true"
            className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-section font-semibold text-white"
          >
            {initials(user.displayName)}
          </span>
          <div className="min-w-0 grow basis-48">
            <p className="text-section font-semibold text-default-900">{user.displayName}</p>
            <p className="break-all text-body text-default-600" dir="ltr">
              <bdi>{user.email}</bdi>
            </p>
          </div>
          <dl className="grid min-w-0 grow basis-72 grid-cols-1 gap-stack sm:grid-cols-2">
            <ReadOnlyField label={t('profile.laboratory')}>
              {laboratoryName ?? t('common.notAvailable')}
            </ReadOnlyField>
            <ReadOnlyField label={t('profile.role')}>
              <StatusBadge tone="info" label={t(`roles.${user.role}`)} />
            </ReadOnlyField>
          </dl>
          <p className="basis-full text-caption text-default-600">{t('profile.roleHint')}</p>
        </section>

        <div className="grid min-w-0 gap-gutter lg:grid-cols-2">
          {/* 2. Editable details */}
          <ProfileDetails />
          {/* 3. Password */}
          <ChangePassword />
        </div>

        {/* 4. Permissions */}
        <Panel
          title={t('profile.effectivePermissions')}
          description={t('profile.effectivePermissionsHint')}
        >
          <PermissionSummary permissions={user.permissions} />
        </Panel>
      </div>
    </>
  );
}

/** Up to two letters for the avatar, taken from the name as entered. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : parts;
  return letters.map((part) => Array.from(part ?? '')[0] ?? '').join('').toUpperCase();
}

function ProfileDetails() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [preferredLocale, setPreferredLocale] = useState<Locale>(
    (user?.preferredLocale as Locale) ?? locale,
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setPreferredLocale(user.preferredLocale as Locale);
  }, [user]);

  const save = useMutation({
    mutationFn: async () => {
      await authApi.updateOwnProfile({ displayName: displayName.trim(), preferredLocale });
      // Apply the language immediately, so the saved preference and what is on
      // screen agree.
      await changeLanguage(preferredLocale);
      await refreshUser();
    },
    onSuccess: () => {
      setHasSubmitted(false);
      show({ tone: 'success', message: t('common.saved') });
    },
  });

  if (!user) return null;

  const nameError =
    hasSubmitted && displayName.trim().length < 2
      ? displayName.trim() === ''
        ? t('validation.required')
        : t('validation.tooShort')
      : undefined;
  const isDirty =
    displayName.trim() !== user.displayName || preferredLocale !== user.preferredLocale;

  return (
    <Panel>
      <Form
        onSubmit={() => {
          setHasSubmitted(true);
          if (displayName.trim().length < 2) return;
          save.mutate();
        }}
        isSubmitting={save.isPending}
        actions={
          <ActionBar>
            <ActionButton
              priority="primary"
              type="submit"
              isLoading={save.isPending}
              isDisabled={!isDirty}
            >
              {t('profile.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <FormSection title={t('profile.sectionDetails')} columns={1}>
          <TextField
            label={t('profile.displayName')}
            value={displayName}
            onChange={setDisplayName}
            isRequired
            {...(nameError ? { error: nameError } : {})}
          />
          <TextField
            type="email"
            label={t('profile.emailReadOnly')}
            description={t('profile.emailReadOnlyHint')}
            value={user.email}
            onChange={() => undefined}
            textDirection="ltr"
            isReadOnly
          />
          <SelectField
            label={t('profile.language')}
            value={preferredLocale}
            onChange={(next) => {
              if (next) setPreferredLocale(next as Locale);
            }}
            options={ALL_LOCALES.map((value) => ({
              value,
              label: value === 'ar' ? t('common.arabic') : t('common.english'),
            }))}
          />
        </FormSection>
        {save.error ? <ErrorState error={save.error} /> : null}
      </Form>
    </Panel>
  );
}

function ChangePassword() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      authApi.changeOwnPassword({ currentPassword, newPassword: password }),
    onSuccess: () => {
      // Nothing typed here stays on screen once it has been used.
      setCurrentPassword('');
      setPassword('');
      setConfirmation('');
      setHasSubmitted(false);
      setIsDone(true);
    },
  });

  const currentError =
    hasSubmitted && currentPassword === '' ? t('validation.required') : undefined;

  return (
    <Panel>
      <Form
        onSubmit={() => {
          setHasSubmitted(true);
          setIsDone(false);
          if (currentPassword === '' || hasNewPasswordProblem(password, confirmation)) return;
          change.mutate();
        }}
        isSubmitting={change.isPending}
        actions={
          <ActionBar>
            <ActionButton priority="primary" type="submit" isLoading={change.isPending}>
              {t('profile.changePassword')}
            </ActionButton>
          </ActionBar>
        }
      >
        <FormSection
          title={t('profile.sectionPassword')}
          description={t('profile.passwordHint')}
          columns={1}
        >
          <PasswordField
            label={t('password.current')}
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            isRequired
            {...(currentError ? { error: currentError } : {})}
          />
          <NewPasswordFields
            password={password}
            confirmation={confirmation}
            onPasswordChange={setPassword}
            onConfirmationChange={setConfirmation}
            showProblems={hasSubmitted}
          />
        </FormSection>
        {change.error ? <ErrorState error={change.error} /> : null}
        {isDone ? (
          <div role="status">
            <Alert tone="success" title={t('profile.passwordChanged')} />
          </div>
        ) : null}
      </Form>
    </Panel>
  );
}
